import React, { useRef, useState } from 'react'
import { Container, ContainerTable, TableFiltersHeader } from './styles'
import { message } from '~/services'
import { transformDate } from '~/utils'
import { Input, Select } from '~/components/shared'
import { Table } from '~/components/shared/Table'
import { IDiagnosis } from '~/interfaces'
import { useNormalizedFilter } from '~/hooks'

interface RowData {
  cid: string
  description: string
  date: string
  type: 'Agudo' | 'Crônico'
  expandable: boolean
  expandableComponent: JSX.Element | null
}

interface IDiagnosesProps {
  diagnoses: IDiagnosis[]
  isLoading: boolean
  error: string
}

export const DiagnosesList = ({ diagnoses, isLoading, error }: IDiagnosesProps): JSX.Element => {
  const { filter } = useNormalizedFilter()
  const inputFilter = useRef<HTMLInputElement>(null)
  const [allowRefresh, setAllowRefresh] = useState(false)
  const [descriptionFilter, setDescriptionFilter] = useState('')
  const [selectedType, setSelectedType] = useState<'Agudo' | 'Crônico' | 'Todos'>('Todos')

  const filterByType = (diagnoses: IDiagnosis[]): IDiagnosis[] => {
    const isFilterEmpty = selectedType === 'Todos'
    if (isFilterEmpty) return diagnoses

    return diagnoses.filter(diagnose => diagnose.type.includes(selectedType))
  }

  const filterByDescription = (diagnoses: IDiagnosis[]): IDiagnosis[] => {
    const isFilterEmpty = descriptionFilter === ''
    if (isFilterEmpty) return diagnoses

    return filter(diagnoses, {
      filterKey: 'description',
      searchTerm: descriptionFilter
    })
  }

  const parseDiagnosesToRowData = (diagnoses: IDiagnosis[]): RowData[] => {
    const filteredDiagnosis = filterByType(filterByDescription(diagnoses))
    let newData: RowData[] = []

    filteredDiagnosis.forEach(({ description, date, cid, type }) => {
      newData = [
        ...newData,
        {
          cid,
          description,
          type,
          date: transformDate({ date }),
          expandable: false,
          expandableComponent: null
        }
      ]
    })

    return newData
  }

  const handleSearch = text => {
    if (text.target.value !== '') {
      setAllowRefresh(true)
    }
    setDescriptionFilter(text.target.value)
  }

  const setSelectedItem = value => {
    if (value !== 'Todos') {
      setAllowRefresh(true)
    }
    setSelectedType(value as 'Agudo' | 'Crônico' | 'Todos')
  }

  const handleRefresh = () => {
    setDescriptionFilter('')
    setSelectedType('Todos')
    parseDiagnosesToRowData(diagnoses)
    inputFilter.current!.value = ''
    setAllowRefresh(false)
  }

  return (
    <Container>
      <ContainerTable>
        <TableFiltersHeader>
          <Select
            apiCallLoadingTimer={0.5}
            name="select"
            setValue={setSelectedItem}
            value={selectedType}
            options={[
              {
                label: 'Todos',
                value: 'Todos'
              },
              {
                label: 'Agudo',
                value: 'Agudo'
              },
              {
                label: 'Crônico',
                value: 'Crônico'
              }
            ]}
          />
          <Input
            ref={inputFilter}
            icon="search"
            message=""
            name="search_diagnosis"
            placeholder="Pesquisar diagnóstico"
            onChange={handleSearch}
          />
        </TableFiltersHeader>

        <Table
          isLoading={isLoading}
          data={parseDiagnosesToRowData(diagnoses)}
          messageError={error || message('diagnoses.search_not_found')}
          headerLineHeight="30px"
          headerPadding="10px 24px 10px 24px"
          minHeight="0px"
          onRefresh={allowRefresh ? handleRefresh : undefined}
          model={[
            {
              key: 'cid',
              label: message('diagnoses.cid').toUpperCase(),
              sortable: true,
              width: '100px'
            },
            {
              key: 'description',
              label: message('diagnoses.description').toUpperCase(),
              sortable: true
            },
            {
              key: 'type',
              label: message('diagnoses.type').toUpperCase(),
              sortable: true,
              width: '180px'
            },
            {
              key: 'date',
              label: message('diagnoses.date').toUpperCase(),
              sortable: true,
              width: '170px'
            }
          ]}
        />
      </ContainerTable>
    </Container>
  )
}
